import { FirebaseApp } from "firebase/app";
import {
  Auth,
  CompleteFn,
  ErrorFn,
  NextOrObserver,
  onAuthStateChanged,
  Unsubscribe,
  User,
} from "firebase/auth";
import { Database, get, ref, runTransaction, set } from "firebase/database";

export type EquestrainResponse<T> = {
  data: T | null;
  error: Error | null;
  statusCode: number | null;
  nativeResponse: Response | null;
};

export type BaseClientOptions = {
  /* The base URL of the Equestrain API (firebase functions). Required. */
  baseUrl: string;
  firebaseAuth: Auth;
  firebaseDB: Database;
  firebaseApp: FirebaseApp;
};

export type CustomRequestParams = {
  skipOnDataLoadingFunctions?: boolean;
};
export type CustomRequestInit = RequestInit & CustomRequestParams;

export class BaseClient {
  protected baseUrl: string;
  protected token?: string;
  protected firebaseAuth: Auth;
  protected firebaseDB: Database;
  protected firebaseApp: FirebaseApp;
  protected onDataLoadingStart: () => void = () => undefined;
  protected onDataLoadingEnd: () => void = () => undefined;
  private skipOnDataLoadingFunctions: boolean = false;
  constructor(options: BaseClientOptions) {
    let url = options.baseUrl;
    if (url.endsWith("/")) {
      url = url.slice(0, -1);
    }
    this.baseUrl = url;
    this.firebaseAuth = options.firebaseAuth;
    this.firebaseDB = options.firebaseDB;
    this.firebaseApp = options.firebaseApp;
  }

  protected setToken(token: string) {
    this.token = token;
  }

  setOnDataLoadingStart(onDataLoadingStart: () => void) {
    this.onDataLoadingStart = onDataLoadingStart;
  }
  setOnDataLoadingEnd(onDataLoadingEnd: () => void) {
    this.onDataLoadingEnd = onDataLoadingEnd;
  }

  setSkipOnDataLoadingFunctions(skip: boolean) {
    this.skipOnDataLoadingFunctions = skip;
  }

  onFirebaseAuthStateChanged = (
    nextOrObserver: NextOrObserver<User>,
    error?: ErrorFn,
    completed?: CompleteFn
  ): Unsubscribe => {
    return onAuthStateChanged(
      this.firebaseAuth,
      nextOrObserver,
      error,
      completed
    );
  };

  protected formatData = <T>(data: T, error?: Error | null) => {
    return {
      data,
      error: error,
      statusCode: 0,
      nativeResponse: null,
    } as EquestrainResponse<T>;
  };

  protected withSetDataLoading = async <T>(
    func: () => Promise<EquestrainResponse<T>>
  ) => {
    try {
      if (!this.skipOnDataLoadingFunctions) {
        this.onDataLoadingStart();
      }
      return await func();
    } catch (error) {
      return this.formatData(null, error as Error);
    } finally {
      if (!this.skipOnDataLoadingFunctions) {
        this.onDataLoadingEnd();
      }
    }
  };

  protected fetchSnapshot = async (
    path: string,
    customReqParams?: CustomRequestParams
  ) => {
    const { skipOnDataLoadingFunctions } = customReqParams || {};
    if (!this.skipOnDataLoadingFunctions && !skipOnDataLoadingFunctions) {
      this.onDataLoadingStart();
    }
    try {
      const snapshot = await get(ref(this.firebaseDB, path));
      return { snapshot, error: null };
    } catch (error) {
      return { snapshot: null, error: error as Error };
    } finally {
      if (!this.skipOnDataLoadingFunctions && !skipOnDataLoadingFunctions) {
        this.onDataLoadingEnd();
      }
    }
  };

  protected writeDataToFirebase = async (
    path: string,
    data: any,
    customReqParams?: CustomRequestParams
  ) => {
    const { skipOnDataLoadingFunctions } = customReqParams || {};
    if (!this.skipOnDataLoadingFunctions && !skipOnDataLoadingFunctions) {
      this.onDataLoadingStart();
    }
    try {
      await set(ref(this.firebaseDB, path), data);
      return { success: true, error: null };
    } catch (error) {
      return { success: false, error: error as Error };
    } finally {
      if (!this.skipOnDataLoadingFunctions && !skipOnDataLoadingFunctions) {
        this.onDataLoadingEnd();
      }
    }
  };

  protected updateFirebaseData = async (
    path: string,
    id: string,
    remove: boolean,
    customReqParams?: CustomRequestParams
  ) => {
    const { skipOnDataLoadingFunctions } = customReqParams || {};
    if (!this.skipOnDataLoadingFunctions && !skipOnDataLoadingFunctions) {
      this.onDataLoadingStart();
    }
    try {
      await runTransaction(ref(this.firebaseDB, path), (currentData) => {
        if (currentData === null) {
          return remove ? null : { 0: id }; // Initialize with numeric key
        }
        const dataArray = Object.values(currentData) as string[]; // Convert object to array and cast to string[]
        if (remove) {
          const updatedArray = (dataArray as string[]).filter(
            (item: string) => item !== id
          );
          return updatedArray.reduce(
            (acc: Record<number, string>, item, index) => {
              acc[index] = item as string; // Re-index the array with explicit cast
              return acc;
            },
            {} as Record<number, string>
          );
        } else {
          if (!dataArray.includes(id)) {
            dataArray.push(id);
          }
          return dataArray.reduce(
            (acc: Record<number, string>, item, index) => {
              acc[index] = item; // Re-index the array
              return acc;
            },
            {} as Record<number, string>
          );
        }
      });
      return { success: true }; // Indicate success
    } catch (error) {
      return { error: error as Error }; // Return the error
    } finally {
      if (!this.skipOnDataLoadingFunctions && !skipOnDataLoadingFunctions) {
        this.onDataLoadingEnd();
      }
    }
  };

  protected firebaseRef = (path: string) => {
    return ref(this.firebaseDB, path);
  };

  protected async fetch<T>(
    relativeUrl: string,
    requestInit?: CustomRequestInit | undefined
  ): Promise<EquestrainResponse<T>> {
    const { skipOnDataLoadingFunctions, ...init } = requestInit || {};
    const token = await this.firebaseAuth.currentUser?.getIdToken();
    if (token) {
      this.setToken(token);
    }
    if (!this.skipOnDataLoadingFunctions && !skipOnDataLoadingFunctions) {
      this.onDataLoadingStart();
    }
    let defaultRequestInit: RequestInit = {
      headers: {
        "Content-Type": "application/json",
        Accept: "application/json",
      },
    };

    if (this.token) {
      defaultRequestInit.headers = {
        ...defaultRequestInit.headers,
        Authorization: this.token,
      };
    }

    const reqInit = {
      ...defaultRequestInit,
      ...init,
    };

    let url = this.baseUrl;
    if (!relativeUrl.startsWith("/")) {
      url = url + "/" + relativeUrl;
    } else {
      url = url + relativeUrl;
    }
    if (url.endsWith("/")) {
      url = url.slice(0, -1);
    }
    try {
      const res = await fetch(url, reqInit);
      if (res.ok) {
        if (res.headers.get("Content-Type")?.includes("application/json")) {
          return {
            data: await res.json(),
            error: null,
            statusCode: res.status,
            nativeResponse: res,
          };
        }
        return {
          data: (await res.text()) as T,
          error: null,
          statusCode: res.status,
          nativeResponse: res,
        };
      }
      return {
        data: null,
        error: new Error(res.statusText),
        statusCode: res.status,
        nativeResponse: res,
      };
    } catch (error) {
      return {
        data: null,
        error: error as Error,
        statusCode: null,
        nativeResponse: null,
      };
    } finally {
      if (!this.skipOnDataLoadingFunctions && !skipOnDataLoadingFunctions) {
        this.onDataLoadingEnd();
      }
    }
  }
}
